import classNames from "classnames"
import React, { useCallback, useMemo } from "react"

import { Analytics } from "@bounce/analytics"
import { DateTime } from "@bounce/date"
import { Label2 } from "@bounce/design-system"
import { useTranslation, useLocale, Trans } from "@bounce/i18n"
import {
  isOpenNow,
  parseOpeningHours,
  parseOpeningHoursExceptions,
  getOpensAtTime,
  getClosesAtTime,
  is24Hour,
} from "@bounce/store"
import { PNImage } from "@bounce/web-components"

import { StoreRating } from "../../../Store/StoreRating/StoreRating"
import SafeLink from "@/components/Generic/SafeLink/SafeLink"
import { InteractionEvents } from "@/libs/analytics/events"
import { getStoreThumbnail } from "@/libs/bounce/stores/getStoreThumbnail"
import type { NearbyStorageSpot } from "@/libs/bounce/stores/types"

import ArrowRight from "@bounce/assets/icons/ArrowRight.svg"

type StorePreviewCardProps = {
  className?: string
  href: string
  store: NearbyStorageSpot
}

const StorePreviewCard = ({ store, className, href }: StorePreviewCardProps) => {
  const { t } = useTranslation()
  const { locale } = useLocale()

  const today = DateTime.local()
  const parsedOpeningHours = parseOpeningHours(store.openingHours)
  const parsedOpeningHourExceptions = parseOpeningHoursExceptions(store.openingHourExceptions)
  const isOpen24hours = is24Hour(parsedOpeningHours)

  const isOpen = isOpenNow(store)
  let statusText
  if (isOpen24hours) {
    statusText = t("cmp.StorePreviewCardV2.open24HoursV2", "Open 24 hours")
  } else if (isOpen) {
    statusText = t("cmp.StorePreviewCardV2.open", "Open")
  } else {
    statusText = t("cmp.StorePreviewCardV2.closed", "Closed")
  }

  // note: this approach does not take some corner cases into account - refactor if experiment turns out to be successful
  const openingHoursText = useMemo(() => {
    if (isOpen24hours) return null

    const openTime = getOpensAtTime({
      currentDate: today,
      openingHours: parsedOpeningHours,
      openingHourExceptions: parsedOpeningHourExceptions,
      locale,
    })

    const closeTime = getClosesAtTime({
      currentDate: today,
      openingHours: parsedOpeningHours,
      openingHourExceptions: parsedOpeningHourExceptions,
      locale,
    })

    if (!openTime || !closeTime) return null

    return (
      <Trans
        ns="marketing-ls-store"
        i18nKey="cmp.StorePreviewCardV2.openingHours"
        defaults="· {{openTime}} - {{closeTime}}"
        values={{ openTime, closeTime }}
        components={{ br: <br /> }}
      />
    )
  }, [isOpen24hours, locale, today, parsedOpeningHours, parsedOpeningHourExceptions])

  const thumbnailHref = getStoreThumbnail(store, { width: 40, height: 40 })

  const onClick = useCallback(
    (ev: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
      ev.preventDefault()
      Analytics.track(InteractionEvents.MapTooltipCtaPressed, { destination: href, slug: store.slug })
      location.assign(href)
    },
    [href],
  )

  return (
    <div
      className={classNames(
        "relative w-[300px] rounded-2xl bg-white p-2 shadow md:w-[300px]",
        "border border-gray-100",
        className,
      )}
      data-testid="store-preview-card-marketing">
      <div className="flex gap-2">
        <div className="flex min-w-0 flex-1 items-start gap-2">
          <PNImage
            src={thumbnailHref}
            height={40}
            width={40}
            sizes="max-width: 40px"
            className="object-fit block size-10 shrink-0 rounded-md"
          />
          <div className="min-w-0">
            <Label2 numberOfLines={1} className="mr-2 block truncate">
              {store.nickname}
            </Label2>
            <div className="pb-1 pt-0.5">
              <Label2
                className={classNames("mr-1 !font-medium", {
                  "text-green-700": isOpen,
                  "text-red-700": !isOpen && !isOpen24hours,
                })}>
                {statusText}
              </Label2>
              {!isOpen24hours && <Label2 className="!font-medium text-neutral-800">{openingHoursText}</Label2>}
            </div>
            <StoreRating store={store} />
          </div>
        </div>
        <div className="shrink-0 self-center">
          <SafeLink href={href} onClick={onClick} target="_blank">
            <button className="radius-xl flex size-[36px] items-center justify-center rounded-full bg-primary-700">
              <ArrowRight color="white" size="4" />
            </button>
          </SafeLink>
        </div>
      </div>
      <div className="absolute bottom-0 left-1/2 -mb-2 size-4 -translate-x-1/2 rotate-45 transform overflow-hidden border-b border-r border-gray-100 bg-white" />
    </div>
  )
}

export default StorePreviewCard
